/**
 * useScrollToTop Hook
 * 
 * Custom React hook that automatically scrolls to the top of the page
 * whenever the route changes. This provides a better user experience
 * by ensuring users start at the top of new content when navigating
 * between pages.
 * 
 * Features:
 * - Automatically triggers on route pathname changes
 * - Uses a utility function with delayed scrolling for reliability
 * - Works across different browsers and scenarios
 * 
 * Usage:
 * ```tsx
 * const MyComponent = () => {
 *   useScrollToTop();
 *   return <div>Content</div>;
 * };
 * ```
 * 
 * @returns void - This hook doesn't return any value, it just performs a side effect
 * 
 * @example
 * // Place in ScrollToTop component or App component
 * export const ScrollToTop = () => {
 *   useScrollToTop();
 *   return null;
 * };
 * 
 * @hook
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollToTopDelayed } from '../utils/scrollUtils';

export const useScrollToTop = () => {
  // Get current location from React Router to detect route changes
  const location = useLocation();

  /**
   * Effect hook that runs whenever the pathname changes.
   * Triggers scroll to top behavior when user navigates to a new route.
   */
  useEffect(() => {
    // Use the utility function with a delay (0ms here) to ensure
    // the scroll happens after the page content is rendered
    // This provides more reliable scroll behavior across different scenarios
    scrollToTopDelayed(0);
  }, [location.pathname]); // Dependency on pathname ensures it runs on route changes
};
